import { useEffect } from "react";

/** Set/restore <title>, <meta> tags, and an optional <link rel="canonical"> for the current page.
 * - Meta tags are addressed by `property=` (OG) or `name=` (twitter/description/etc).
 * - Existing tags from index.html are mutated and restored on unmount.
 * - New tags created here are removed on unmount. */
export default function useDocumentMeta({ title, metas = [], canonical } = {}) {
  const metasKey = JSON.stringify(metas);
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const parsedMetas = JSON.parse(metasKey);
    const touched = [];
    parsedMetas.forEach(({ property, name, content }) => {
      if (!content) return;
      const selector = property ? `meta[property="${property}"]` : `meta[name="${name}"]`;
      let el = document.head.querySelector(selector);
      let createdNow = false;
      if (!el) {
        el = document.createElement("meta");
        if (property) el.setAttribute("property", property);
        if (name) el.setAttribute("name", name);
        el.setAttribute("data-dynamic", "true");
        document.head.appendChild(el);
        createdNow = true;
      }
      const previousContent = el.getAttribute("content");
      el.setAttribute("content", content);
      touched.push({ el, createdNow, previousAttr: "content", previousValue: previousContent });
    });

    // Canonical link
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      let createdNow = false;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
        createdNow = true;
      }
      const previousHref = link.getAttribute("href");
      link.setAttribute("href", canonical);
      touched.push({ el: link, createdNow, previousAttr: "href", previousValue: previousHref });
    }

    return () => {
      document.title = prevTitle;
      touched.forEach(({ el, createdNow, previousAttr, previousValue }) => {
        if (createdNow) {
          el.parentNode?.removeChild(el);
        } else if (previousValue != null) {
          el.setAttribute(previousAttr, previousValue);
        }
      });
    };
  }, [title, metasKey, canonical]);
}
